import { Injectable } from "@angular/core";
import { ChartsDataBySubscription, ChartsDataByDay, FinOpsAzureCostService, FinOpsCounters, HistoricalDataByDate } from "./finops-azure-services.model";
import { DateGranularityEnum } from "src/app/shared/enum/date-granularity.enum";
import { Filter } from "src/app/store/preferences/filters/preferences-filters.model";
import { FinOpsChartHistoricalData, FinOpsChartHistoricalItem } from "src/app/shared/component/chart-finops-historical/chart-finops-historical.component";
import { FinOpsChartCompareData, FinOpsChartCompareItem } from "src/app/shared/component/chart-finops-compare/chart-finops-compare.component";
import { FloorPipe } from "src/app/shared/pipe/floor.pipe";
import { DateService } from "src/app/shared/service/date.service";
import { TranslateService } from "@ngx-translate/core";
import { ColorEnum } from "src/app/shared/enum/color.enum";
import { FormatNumberPipe } from "src/app/shared/pipe/format-number.pipe";
import { AzureConfig } from "src/app/store/preferences/providers/preferences-providers.model";

export type DataForDatatableCompute = { 
    measuresDatatable: { [id: string]: FinOpsAzureCostService[]; },
    filteredCounters: { [id: string]: Record<string, FinOpsCounters>; },
    previousTotalCounters: { [id: string]: FinOpsCounters; }
}

@Injectable({
    providedIn: 'root'
})
export class FinOpsAzureServicesHelper {

    // helper: recompute gap fields from cost & previous_cost
    static mergeCounters(a: FinOpsCounters | undefined, b: FinOpsCounters): FinOpsCounters {
        if (!a) {
            // clone b, but zero out derived fields; we’ll recompute gap_* later
            return {
                prediction: b.prediction,
                previous_cost: b.previous_cost,
                cost: b.cost,
                day_ratio: b.day_ratio,
                gap_ratio: 0,
                gap_value: 0
            };
        }

        return {
            prediction: a.prediction || b.prediction,
            previous_cost: a.previous_cost + b.previous_cost,
            cost: a.cost + b.cost,
            day_ratio: a.day_ratio || b.day_ratio, // keep first non-zero (typical for same date)
            gap_ratio: 0, // recompute later
            gap_value: 0
        };
    }

    // helper: recompute gap fields from cost & previous_cost
    static finalizeGaps(c: FinOpsCounters): void {
        const gap = c.cost - c.previous_cost;
        c.gap_value = gap;
        c.gap_ratio = c.previous_cost === 0 ? 100 : (gap / c.previous_cost) * 100;
    }

    static computeDatatableGrandTotal(perSubscription: Record<string, DataForDatatableCompute>): DataForDatatableCompute {
        const out: DataForDatatableCompute = {
            measuresDatatable: {},
            filteredCounters: {},
            previousTotalCounters: {}
        };

        for (const [, data] of Object.entries(perSubscription)) {
            // 1) Merge measuresDatatable (concat rows per date)
            for (const [date, rows] of Object.entries(data.measuresDatatable)) {
            (out.measuresDatatable[date] ??= []).push(...rows);
            }

            // 2) Merge filteredCounters (sum per date + granularity)
            for (const [date, byGranularity] of Object.entries(data.filteredCounters)) {
            const dstByGran = (out.filteredCounters[date] ??= {});
            for (const [granularity, counters] of Object.entries(byGranularity)) {
                dstByGran[granularity] = FinOpsAzureServicesHelper.mergeCounters(dstByGran[granularity], counters);
            }
            }

            // 3) Merge previousTotalCounters (sum per granularity)
            for (const [granularity, counters] of Object.entries(data.previousTotalCounters)) {
            out.previousTotalCounters[granularity] =
                FinOpsAzureServicesHelper.mergeCounters(out.previousTotalCounters[granularity], counters);
            }
        }

        // 4) Recompute gaps after all sums
        for (const byGranularity of Object.values(out.filteredCounters)) {
            for (const c of Object.values(byGranularity)) {
                FinOpsAzureServicesHelper.finalizeGaps(c);
            }
        }
        for (const c of Object.values(out.previousTotalCounters)) {
            FinOpsAzureServicesHelper.finalizeGaps(c);
        }

        return out;
    }

    static computeDatatable(historicalDatesSelected: string[], historicalDataByGranularity: HistoricalDataByDate, filters: Filter[]): DataForDatatableCompute {
        // compute data for datatable
        let measuresDatatable: { [id: string]: FinOpsAzureCostService[]; } = {}
        let filteredCounters: { [id: string]: Record<string, FinOpsCounters>; } = {}
        let previousTotalCounters: { [id: string]: FinOpsCounters; } = {}

        for(const date of historicalDatesSelected) {
            let datatable: FinOpsAzureCostService[] = []
            Object.values(historicalDataByGranularity[date]).forEach(costService => {
                datatable.push(costService)
            });
            filters.forEach(filter => {
                if (Array.isArray(filter.value)) {
                    datatable = datatable.filter((costService) => filter.value.includes(costService[filter.key as keyof FinOpsAzureCostService]))
                } else {
                    datatable = datatable.filter((costService) => costService[filter.key as keyof FinOpsAzureCostService] === filter.value)
                }
            })
            measuresDatatable[date] = datatable

            // Update counters for the measure
            let totalCounters: Record<DateGranularityEnum, FinOpsCounters> = {
                "year": {
                    prediction: false,
                    previous_cost: 0,
                    cost: 0,
                    day_ratio: 0,
                    gap_ratio: 0,
                    gap_value: 0
                },
                "month": {
                    prediction: false,
                    previous_cost: 0,
                    cost: 0,
                    day_ratio: 0,
                    gap_ratio: 0,
                    gap_value: 0
                },
                "day": {
                    prediction: false,
                    previous_cost: 0,
                    cost: 0,
                    day_ratio: 0,
                    gap_ratio: 0,
                    gap_value: 0
                }
            }
            datatable.forEach(row => {
                Object.entries(row.costs_counters).forEach(entries => {
                    let granularity = entries[0] as DateGranularityEnum
                    let counters = entries[1]
                    totalCounters[granularity].prediction = counters.prediction
                    totalCounters[granularity].cost += Number(counters.cost)
                    totalCounters[granularity].day_ratio = Number(counters.day_ratio)
                });
            });
            Object.keys(totalCounters).forEach(granularity => {
                totalCounters[granularity as DateGranularityEnum].cost = totalCounters[granularity as DateGranularityEnum].cost
                totalCounters[granularity as DateGranularityEnum].day_ratio = totalCounters[granularity as DateGranularityEnum].day_ratio
                
                if (Object.keys(previousTotalCounters).length > 0) {
                    totalCounters[granularity as DateGranularityEnum].previous_cost = previousTotalCounters[granularity].cost
                    let gap_value = totalCounters[granularity as DateGranularityEnum].cost - totalCounters[granularity as DateGranularityEnum].previous_cost
                    totalCounters[granularity as DateGranularityEnum].gap_ratio = totalCounters[granularity as DateGranularityEnum].previous_cost == 0 ? 100 : ((gap_value / totalCounters[granularity as DateGranularityEnum].previous_cost) * 100)
                    totalCounters[granularity as DateGranularityEnum].gap_value = gap_value
                }
            });
            filteredCounters[date] = totalCounters
            previousTotalCounters = totalCounters
        }
        return {
            measuresDatatable: measuresDatatable,
            filteredCounters: filteredCounters,
            previousTotalCounters: previousTotalCounters
        }
    }

    static computeCompareChartData(
        subscriptionChartsData: ChartsDataByDay,
        targetDate:string,
        granularity: DateGranularityEnum,
        filters: Filter[],
        floorPipe: FloorPipe,
        numberFormatPipe: FormatNumberPipe,
        dateService: DateService
    ): FinOpsChartCompareData {

        // Init agregator
        let filteredServices: FinOpsAzureCostService[] = []

        // Init sections for return object
        let filteredCompareChartData: FinOpsChartCompareData = {
            costs:[],
            minCostRatioString: "",
            minCostValueString: "",
            prediction: false,
            dateString: ""
        }

        let compareMinCostValue: number = 0.0
        let compareMinCostRatio: number = 0.01
        let compareChartCosts: FinOpsChartCompareItem[] = []

        // Retrieve all services from measure
        subscriptionChartsData[targetDate].forEach(measure => {
            filteredServices.push(measure)
        })

        // Look at all selected filters, and remove from "filteredServices" not selected services.
        filters.forEach(filter => {
            if (Array.isArray(filter.value)) {
                filteredServices = filteredServices.filter((costService) => filter.value.includes(costService[filter.key as keyof FinOpsAzureCostService]))
            } else {
                filteredServices = filteredServices.filter((costService) => costService[filter.key as keyof FinOpsAzureCostService] === filter.value)
            }
        })

        let totalCosts: number = 0
        let prediction: boolean = false

        // Iterate over services list to retrieve total cost for selected services
        filteredServices.forEach(service => {
            totalCosts += Number(service.costs_counters[granularity].cost)
            prediction = service.costs_counters[granularity].prediction
        });

        compareMinCostValue = totalCosts * compareMinCostRatio
        filteredServices.forEach(service => {
            let serviceCost = Number(service.costs_counters[granularity].cost)
            if (serviceCost > compareMinCostValue) {
                const colorKey = 'charts_color_' + compareChartCosts.length
                compareChartCosts.push(
                    {
                        category: service.id,
                        cost: parseFloat(floorPipe.transform(serviceCost, 2)),
                        color: ColorEnum[colorKey as keyof typeof ColorEnum]
                    }
                )
            }
        });

        // Update properties
        filteredCompareChartData.minCostRatioString = compareMinCostRatio == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(compareMinCostRatio * 100, 2))
        filteredCompareChartData.minCostValueString = compareMinCostValue == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(compareMinCostValue, 2))
        filteredCompareChartData.dateString = dateService.formatLongDate(targetDate)
        filteredCompareChartData.prediction = prediction
        filteredCompareChartData.costs = compareChartCosts.sort((a, b) => b["cost"] - a["cost"])

        return filteredCompareChartData
    }

    static computeAggregatedCompareChartData(
        azureConfig: AzureConfig,
        bulkChartsData: ChartsDataBySubscription,
        compareDate: string,
        granularity: DateGranularityEnum,
        filters: Filter[],
        floorPipe: FloorPipe,
        numberFormatPipe: FormatNumberPipe,
        dateService: DateService
    ): FinOpsChartCompareData {
        const compareMinCostRatio:number = 0.01; // keep same as single-subscription helper
        const groupedCosts: Record<string, number> = {};    // category (service.id) -> summed cost
        let totalCosts = 0;
        let prediction = false;

        // Iterate available subscriptions
        for (const subscription of azureConfig.subscriptions) {
            const rowsForDate = bulkChartsData[subscription.subscription_id]?.[compareDate] ?? [];
            if (!rowsForDate.length) continue;

            // Apply filters BEFORE aggregation
            let filtered = [...rowsForDate];
            for (const filter of filters) {
                if (Array.isArray(filter.value)) {
                    filtered = filtered.filter(svc =>
                    (filter.value as any[]).includes(svc[filter.key as keyof FinOpsAzureCostService])
                    );
                } else {
                    filtered = filtered.filter(
                    svc => svc[filter.key as keyof FinOpsAzureCostService] === filter.value
                    );
                }
            }

            // Sum filtered costs + OR prediction, and group by category
            for (const svc of filtered) {
                const cost = Number(svc.costs_counters[granularity].cost);
                totalCosts += cost;
                prediction = prediction || svc.costs_counters[granularity].prediction;

                const category = svc.id; // same category as in _computeCompareChartData
                groupedCosts[category] = (groupedCosts[category] ?? 0) + cost;
            }
        }

        // Compute global threshold based on filtered total
        const minCostValue = totalCosts * compareMinCostRatio;

        // Build costs array (optionally sorted for stable coloring)
        const costs: FinOpsChartCompareItem[] = Object.entries(groupedCosts)
            .filter(([, cost]) => cost > minCostValue)
            .sort((a, b) => b[1] - a[1])
            .map(([category, cost], index) => {
                const colorKey = `charts_color_${index}` as keyof typeof ColorEnum;
                return {
                    category,
                    cost: parseFloat(floorPipe.transform(cost, 2)),
                    color: ColorEnum[colorKey],
                };
            });

        return {
            costs,
            minCostRatioString:
            compareMinCostRatio === 0
                ? ""
                : numberFormatPipe.transform(floorPipe.transform(compareMinCostRatio * 100, 2)),
            minCostValueString:
            minCostValue === 0
                ? ""
                : numberFormatPipe.transform(floorPipe.transform(minCostValue, 2)),
            prediction,
            dateString: dateService.formatLongDate(compareDate),
        };
    }

    static computeHistoricalChartData(
        historicalDatesSelected: string[],
        subscriptionChartsData: ChartsDataByDay,
        filters: Filter[],
        granularity: DateGranularityEnum,
        floorPipe: FloorPipe,
        numberFormatPipe: FormatNumberPipe,
        translateService: TranslateService,
        dateService: DateService
    ): FinOpsChartHistoricalData {

        // Init sections
        let prediction: boolean = false
        let historicalChartCosts: FinOpsChartHistoricalItem[] = []
        let historicalChartMinCostRatio: number = 0.0
        let historicalChartMinCostValue: number = 0.0

        // 
        let filteredHistoricalChartData: FinOpsChartHistoricalData = {
            minCostRatioString: "",
            minCostValueString: "",
            prediction: false,
            costs: []
        }

        // Calculate charts for selected dates
        historicalDatesSelected.forEach((dateKey) => {
            let filteredServices: FinOpsAzureCostService[] = []
            subscriptionChartsData[dateKey].forEach(measure => {
                filteredServices.push(measure)
            })
            filters.forEach(filter => {
                if (Array.isArray(filter.value)) {
                    filteredServices = filteredServices.filter((costService) => filter.value.includes(costService[filter.key as keyof FinOpsAzureCostService]))
                } else {
                    filteredServices = filteredServices.filter((costService) => costService[filter.key as keyof FinOpsAzureCostService] === filter.value)
                }
            });
            // Compute historical chart data for the current date
            let totalCosts: number = 0
            prediction = false
            filteredServices.forEach(service => {
                totalCosts += Number(service.costs_counters[granularity].cost)
                prediction = service.costs_counters[granularity].prediction
            });

            historicalChartCosts.push(
                {
                    date: new Date(dateKey),
                    cost: parseFloat(floorPipe.transform(totalCosts, 2)),
                    dateString: dateService.formatLongDate(dateKey),
                    predictionString: prediction ? translateService.instant("FINOPS.PREDICTION") + "\n\n" : ""
                }
            )
        })

        // Update properties
        filteredHistoricalChartData.minCostRatioString = historicalChartMinCostRatio == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(historicalChartMinCostRatio * 100, 2))
        filteredHistoricalChartData.minCostValueString = historicalChartMinCostValue == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(historicalChartMinCostValue, 2))
        filteredHistoricalChartData.prediction = prediction
        filteredHistoricalChartData.costs = historicalChartCosts

        return filteredHistoricalChartData
    }

    static computeAggregatedHistoricalChartData(
        azureConfig: AzureConfig,
        historicalDatesSelected: string[],
        bulkChartsData: ChartsDataBySubscription,
        filters: Filter[],
        granularity: DateGranularityEnum,
        floorPipe: FloorPipe,
        translateService: TranslateService,
        dateService: DateService
    ): FinOpsChartHistoricalData {

        let prediction = false;
        const aggregatedCosts: FinOpsChartHistoricalItem[] = [];

        // Iterate over all selected dates
        historicalDatesSelected.forEach((dateKey) => {
            let totalCostForDate = 0;
            let datePrediction = false;

            // Iterate over all subscriptions
            for (const subscription of azureConfig.subscriptions) {
                const subscriptionData = bulkChartsData[subscription.subscription_id];
                if (!subscriptionData || !subscriptionData[dateKey]) continue;

                // Apply filters per subscription
                let filteredServices = [...subscriptionData[dateKey]];
                filters.forEach((filter) => {
                    if (Array.isArray(filter.value)) {
                        filteredServices = filteredServices.filter((svc) =>
                            filter.value.includes(svc[filter.key as keyof FinOpsAzureCostService])
                        );
                    } else {
                        filteredServices = filteredServices.filter(
                            (svc) => svc[filter.key as keyof FinOpsAzureCostService] === filter.value
                        );
                    }
                });

                // Sum costs for this date
                filteredServices.forEach((service) => {
                    totalCostForDate += Number(service.costs_counters[granularity].cost);
                    datePrediction = datePrediction || service.costs_counters[granularity].prediction;
                });
            }

            // Add aggregated data for this date
            aggregatedCosts.push({
                date: new Date(dateKey),
                cost: parseFloat(floorPipe.transform(totalCostForDate, 2)),
                dateString: dateService.formatLongDate(dateKey),
                predictionString: datePrediction ? translateService.instant("FINOPS.PREDICTION") + "\n\n" : "",
            });

            prediction = prediction || datePrediction;
        });

        return {
            costs: aggregatedCosts,
            prediction,
            minCostRatioString:"",
            minCostValueString:""
        };
    }

}